import { useEffect, useState } from "react";
import {
  CheckIcon,
  Loader2Icon,
  PlusIcon,
  RefreshCwIcon,
  RocketIcon,
  SearchIcon,
  MoonIcon,
  SunIcon,
} from "lucide-react";
import { toast } from "sonner";
import { EFFORTS, type PriceDocument } from "@woodpecker/protocol";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Desktop,
  MobileItem,
  MobileList,
  PageHeader,
} from "../ui/card";
import { Table, TBody, TD, TH, THead, TR } from "../ui/table";
import { Tabs, TabsList, TabsTrigger } from "../ui/overlay";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input, Label } from "../ui/form";
import { cn } from "../lib/cn";
import { round2, maxOf } from "../lib/format";
import { useT, useTT } from "../i18n";
import { useStore } from "../store/store";
import { useEnsurePayerCap, useEnsureSessions } from "../components/WalletBlock";
import type { ServiceEntry } from "../adapters/chain";
import { errorText } from "../adapters/clients";
import {
  BatchLine,
  Kinds,
  Labels,
  ManageButton,
  MineBadge,
  PriceLine,
  RateLine,
  ServiceName,
  SignedOut,
} from "../components/common";
import { modelsOf } from "./Conversations";

const TABLE = "min-w-[57rem] table-fixed";
const LAST = "w-[13.25rem]";
const CELL = "align-top whitespace-normal";
const PAD = "pt-1";

/** Whether the price document's hash is committed in the entry (issue #3 item 11): the directory's answer to "is this the price?". */
export function PriceCommitmentText({ entry, className }: { entry: ServiceEntry; className?: string }) {
  const t = useT();
  const c = entry.priceCommitment;
  return (
    <span
      className={cn(
        "text-xs",
        c === "committed"
          ? "text-emerald-600 dark:text-emerald-400"
          : c === "changed"
            ? "text-amber-600 dark:text-amber-400"
            : "text-muted-foreground",
        className,
      )}
      title={c === "none" ? undefined : entry.pricingHash}
    >
      {t(`state.price.${c}`)}
    </span>
  );
}

function StateText({ entry, stacked = false }: { entry: ServiceEntry; stacked?: boolean }) {
  const t = useT();
  if (!entry.reachable) return <span className="text-muted-foreground">{t("services.unreachable")}</span>;
  if (!entry.ready) return <span className="text-muted-foreground">{t("state.ready")} · —</span>;
  return stacked ? (
    <span className="flex flex-col text-emerald-600 dark:text-emerald-400">
      <span className="inline-flex items-center gap-1">
        <CheckIcon className="size-3.5" />
        {t("state.ready")}
      </span>
      <span className="font-mono text-xs wrap-anywhere">{entry.token}</span>
      <PriceCommitmentText entry={entry} />
    </span>
  ) : (
    <span className="inline-flex flex-wrap items-center gap-x-1 text-emerald-600 dark:text-emerald-400">
      <CheckIcon className="size-3.5" />
      {t("state.readyToken", { token: entry.token })}
      <span aria-hidden>·</span>
      <PriceCommitmentText entry={entry} />
    </span>
  );
}

function DefaultCapInput({ token }: { token: string }) {
  const { settings, saveSettings, directory } = useStore();
  const t = useT();
  const published = directory.find((e) => e.token === token && e.defaultCap !== undefined)?.defaultCap ?? 5;
  return (
    <Input
      type="number"
      min={0}
      step={1}
      className="h-7 w-16 px-2 text-right tabular-nums"
      aria-label={t("services.my.capFor", { token })}
      placeholder={String(published)}
      value={settings.defaultCaps[token] ?? ""}
      onChange={(e) => {
        const v = e.target.value.trim();
        const caps = { ...settings.defaultCaps };
        if (v === "") delete caps[token];
        else caps[token] = Math.max(0, Math.floor(Number(v) || 0));
        void saveSettings({ defaultCaps: caps }).catch((err: Error) =>
          toast.error(t("settings.saveFailed", { detail: err.message })),
        );
      }}
    />
  );
}

function Published({ entry, className }: { entry: ServiceEntry; className?: string }) {
  const t = useT();
  return entry.defaultCap === undefined ? null : (
    <span className={cn("text-[11px] text-muted-foreground", className)}>
      {t("services.my.published", { n: entry.defaultCap })}
    </span>
  );
}

function MyServices() {
  const { myServices, settings, saveSettings } = useStore();
  const t = useT();
  const remove = (name: string) =>
    void saveSettings({ myServices: settings.myServices.filter((n) => n !== name) }).catch((e: Error) =>
      toast.error(t("settings.saveFailed", { detail: e.message })),
    );
  const actions = (e: ServiceEntry) => (
    <div className="flex justify-end gap-1">
      <ManageButton entry={e} />
      {!e.mine && (
        <Button variant="ghost" size="sm" onClick={() => remove(e.name)}>
          {t("common.remove")}
        </Button>
      )}
    </div>
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("services.my.title")}</CardTitle>
        <CardDescription>{t("services.my.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <MobileList>
          {myServices.map((e) => (
            <MobileItem key={e.name}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <ServiceName entry={e} className="truncate" />
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Labels entry={e} />
                    <MineBadge entry={e} />
                  </div>
                </div>
                <Kinds kinds={e.kinds} />
              </div>
              {e.tool && <span className="font-mono text-xs">{e.tool}</span>}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  <span className="text-muted-foreground">{t("col.defaultCap")}</span>
                  <DefaultCapInput token={e.token} />
                  <span className="font-mono">{e.token}</span>
                </span>
                <Published entry={e} />
                <RateLine token={e.token} buy />
                <span className="ml-auto flex shrink-0 gap-1">{actions(e)}</span>
              </div>
            </MobileItem>
          ))}
          {myServices.length === 0 && (
            <li className="py-6 text-center text-sm text-muted-foreground">{t("services.my.empty")}</li>
          )}
        </MobileList>
        <Desktop>
          <Table className={TABLE}>
            <colgroup>
              <col />
              <col className="w-[14%]" />
              <col className="w-[21%]" />
              <col className="w-[17%]" />
              <col className={LAST} />
            </colgroup>
            <THead>
              <TR>
                <TH>{t("col.gateway")}</TH>
                <TH>{t("col.kind")}</TH>
                <TH>{t("col.toolName")}</TH>
                <TH className="text-right">{t("col.defaultCap")}</TH>
                <TH className="text-right" />
              </TR>
            </THead>
            <TBody>
              {myServices.map((e) => (
                <TR key={e.name}>
                  <TD className={CELL}>
                    <div className={PAD}>
                      <ServiceName entry={e} />
                      <div className="flex flex-wrap items-center gap-1.5 pl-6.5 text-xs text-muted-foreground">
                        <Labels entry={e} />
                        <MineBadge entry={e} />
                      </div>
                    </div>
                  </TD>
                  <TD className={CELL}>
                    <Kinds kinds={e.kinds} className="flex-wrap py-1" />
                  </TD>
                  <TD className={cn(CELL, "font-mono wrap-anywhere")}>
                    <div className={PAD}>{e.tool ?? "—"}</div>
                  </TD>
                  <TD className={CELL}>
                    <div className="flex items-center justify-end gap-1.5">
                      <DefaultCapInput token={e.token} />
                      <span className="w-[4.5rem] font-mono text-muted-foreground">{e.token}</span>
                    </div>
                    <div className="mt-1 text-right">
                      <Published entry={e} />
                    </div>
                    <div className="mt-1 text-right">
                      <RateLine token={e.token} buy className="justify-end text-[11px]" />
                    </div>
                  </TD>
                  <TD className={CELL}>{actions(e)}</TD>
                </TR>
              ))}
              {myServices.length === 0 && (
                <TR>
                  <TD colSpan={5} className="py-8 text-center text-muted-foreground">
                    {t("services.my.empty")}
                  </TD>
                </TR>
              )}
            </TBody>
          </Table>
        </Desktop>
        <div className="mt-4 flex justify-end">
          <Button asChild>
            <a href="#/services/deploy">
              <RocketIcon />
              {t("services.deploy")}
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Directory() {
  const { directory, settings, saveSettings, directoryLoading, refreshDirectory, wpPerToken } = useStore();
  const t = useT();
  const tt = useTT();
  const [q, setQ] = useState("");
  const [checks, setChecks] = useState<Record<string, "checking" | "done">>({});
  const needle = q.trim().toLowerCase();
  const rows = directory.filter(
    (e) =>
      !e.mine &&
      [e.name, ...e.labels, e.token, ...e.kinds, ...e.kinds.map((k) => t(`kind.${k}`))].some((s) =>
        s.toLowerCase().includes(needle),
      ),
  );
  const add = (name: string) => {
    void saveSettings({
      myServices: settings.myServices.includes(name) ? settings.myServices : [...settings.myServices, name],
    })
      .then(() => toast.success(t("services.addedToast", { name })))
      .catch((e: Error) => toast.error(t("settings.saveFailed", { detail: e.message })));
  };
  async function check(e: ServiceEntry) {
    setChecks((c) => ({ ...c, [e.name]: "checking" }));
    try {
      const res = await fetch(`${e.origin}/pricing`);
      const doc = (await res.json()) as PriceDocument;
      toast(t("services.checkResult", { name: e.name, status: res.status, ready: String(doc.ready) }));
    } catch (err) {
      toast.error(
        t("services.checkResult", {
          name: e.name,
          status: "—",
          ready: String(err instanceof Error ? err.message : err),
        }),
      );
    }
    setChecks((c) => ({ ...c, [e.name]: "done" }));
  }
  const wpLine = (e: ServiceEntry) => t("common.wp", { n: round2(maxOf(e.price) * wpPerToken(e.token)) });
  const actions = (e: ServiceEntry) => {
    const added = settings.myServices.includes(e.name);
    return (
      <div className="flex justify-end gap-1.5">
        <Button variant="outline" size="sm" onClick={() => void check(e)} disabled={checks[e.name] === "checking"}>
          {checks[e.name] === "checking" && <Loader2Icon className="animate-spin" />}
          {t("common.check")}
        </Button>
        <Button size="sm" disabled={added} onClick={() => add(e.name)}>
          {added ? <CheckIcon /> : <PlusIcon />}
          {t(added ? "common.added" : "common.add")}
        </Button>
      </div>
    );
  };
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>{t("services.directory.title")}</CardTitle>
            <CardDescription>{t("services.directory.description")}</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => void refreshDirectory()} disabled={directoryLoading}>
            {directoryLoading ? <Loader2Icon className="animate-spin" /> : <RefreshCwIcon />}
            {t("services.refresh")}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="relative sm:max-w-xs">
          <SearchIcon className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-8" placeholder={t("services.search")} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {directoryLoading && directory.length === 0 && (
          <p className="text-sm text-muted-foreground">{t("services.loading")}</p>
        )}
        <MobileList>
          {rows.map((e) => (
            <MobileItem key={e.name}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <ServiceName entry={e} className="truncate" />
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Labels entry={e} />
                  </div>
                </div>
                <Kinds kinds={e.kinds} />
              </div>
              <div className="text-xs text-muted-foreground">
                <PriceLine entry={e} /> <BatchLine entry={e} />
              </div>
              <RateLine token={e.token} buy />
              <div className="flex items-center justify-between gap-3 text-xs">
                <StateText entry={e} />
                {actions(e)}
              </div>
            </MobileItem>
          ))}
          {rows.length === 0 && !directoryLoading && (
            <li className="py-6 text-center text-sm text-muted-foreground">{t("services.noMatch", { q })}</li>
          )}
        </MobileList>
        <Desktop>
          <Table className={TABLE}>
            <colgroup>
              <col />
              <col className="w-[14%]" />
              <col className="w-[21.5%]" />
              <col className="w-[12%]" />
              <col className="w-[9.5%]" />
              <col className={LAST} />
            </colgroup>
            <THead>
              <TR>
                <TH>{t("col.gateway")}</TH>
                <TH>{t("col.token")}</TH>
                <TH>{t("col.price")}</TH>
                <TH>{t("col.kind")}</TH>
                <TH>{t("col.state")}</TH>
                <TH className="text-right" />
              </TR>
            </THead>
            <TBody>
              {rows.map((e) => (
                <TR key={e.name}>
                  <TD className={CELL}>
                    <div className={PAD}>
                      <ServiceName entry={e} />
                      <div className="flex flex-wrap items-center gap-1.5 pl-6.5 text-xs text-muted-foreground">
                        <Labels entry={e} />
                      </div>
                    </div>
                  </TD>
                  <TD className={CELL}>
                    <span className="flex min-h-7 flex-wrap items-center gap-x-1.5">
                      <span className="font-mono">{e.token}</span>
                    </span>
                    <RateLine token={e.token} buy className="text-[11px]" />
                  </TD>
                  <TD className={cn(CELL, "text-muted-foreground")}>
                    <div className={cn(PAD, "text-pretty")}>
                      {tt(priceTextOf(e))} <BatchLine entry={e} />
                    </div>
                    <div className="text-xs text-muted-foreground/80">{wpLine(e)}</div>
                  </TD>
                  <TD className={CELL}>
                    <Kinds kinds={e.kinds} className="flex-wrap py-1" />
                  </TD>
                  <TD className={CELL}>
                    <div className={PAD}>
                      <StateText entry={e} stacked />
                    </div>
                  </TD>
                  <TD className={CELL}>{actions(e)}</TD>
                </TR>
              ))}
              {rows.length === 0 && !directoryLoading && (
                <TR>
                  <TD colSpan={6} className="py-8 text-center text-muted-foreground">
                    {t("services.noMatch", { q })}
                  </TD>
                </TR>
              )}
            </TBody>
          </Table>
        </Desktop>
      </CardContent>
    </Card>
  );
}

import { priceText } from "../lib/format";
const priceTextOf = (e: ServiceEntry) => priceText(e.price, e.token);

export function ServicesPage() {
  const { signedIn } = useStore();
  const t = useT();
  if (!signedIn) {
    return (
      <>
        <PageHeader
          title={t("services.title")}
          description={t("services.description")}
          action={
            <Button disabled>
              <RocketIcon />
              {t("services.deploy")}
            </Button>
          }
        />
        <SignedOut what="signedOut.services" />
      </>
    );
  }
  return (
    <>
      <PageHeader title={t("services.title")} description={t("services.description")} />
      <div className="flex flex-col gap-4">
        <MyServices />
        <Directory />
      </div>
    </>
  );
}

/** The settings record's card (D75): where it lives, the Console's identity that pays its commits, its cap, the last commit. */
function SettingsRecordCard() {
  const { settings, entry, consoleIdentity, lastSettingsWrite, payerAuthorization, headMemory, settingsProblem } =
    useStore();
  const t = useT();
  const memoryName = settingsProblem ? headMemory : settings.defaultMemory || headMemory;
  const e = entry(memoryName);
  const [auth, setAuth] = useState<{ token: string; cap: number; used: number; price: number } | null>(null);
  useEffect(() => {
    void payerAuthorization(memoryName)
      .then(setAuth)
      .catch(() => setAuth(null));
  }, [memoryName, payerAuthorization, lastSettingsWrite]);
  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle>{t("settings.record.title")}</CardTitle>
        <CardDescription>
          {t("settings.record.description", { memory: memoryName, token: e?.token ?? "", price: e?.price.max ?? 1 })}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-1 text-sm">
        <div>
          {t("settings.record.identity")}:{" "}
          <span className="font-mono">
            {consoleIdentity ? consoleIdentity.address : t("settings.record.noIdentity")}
          </span>
        </div>
        <div>
          {auth
            ? t("settings.record.cap", { used: auth.used, cap: auth.cap, token: auth.token })
            : t("settings.record.noCap")}
        </div>
        <div className="text-muted-foreground">
          {lastSettingsWrite
            ? t("settings.record.lastTx", { tx: lastSettingsWrite.tx.slice(0, 10), memory: lastSettingsWrite.memory })
            : t("settings.record.none")}
        </div>
      </CardContent>
    </Card>
  );
}

export function SettingsPage() {
  const { signedIn, myServices, settings, saveSettings, switchMemory, headMemory, settingsProblem, theme, setTheme } =
    useStore();
  const ensureSessions = useEnsureSessions();
  const ensurePayerCap = useEnsurePayerCap();
  const t = useT();
  const runners = myServices.filter((e) => e.kinds.includes("runner"));
  const memories = myServices.filter((e) => e.kinds.includes("memory"));
  const models = modelsOf(myServices);
  // every save is a commit paid by the Console's identity (D75): its cap in the memory's token comes first
  const save = (patch: Parameters<typeof saveSettings>[0]) =>
    void ensurePayerCap([settingsProblem ? headMemory : settings.defaultMemory]).then((ok) => {
      if (!ok) return;
      return saveSettings(patch)
        .then(() => toast.success(t("settings.saved")))
        .catch((e: Error) => toast.error(t("settings.saveFailed", { detail: errorText(e) })));
    });
  // the default memory moves the settings record (issue #2): sign in to the new memory and to the head first. A
  // pointer gone stale (the record's memory left the directory, issue #11 B) is repaired here too: the current value
  // moves the record to this memory
  const switchTo = (name: string) => {
    if (name === settings.defaultMemory && !settingsProblem) return;
    const previous = settingsProblem?.pointer ?? settings.defaultMemory;
    ensureSessions([name, headMemory], (ok) => {
      if (!ok) return;
      void ensurePayerCap([name, headMemory]).then((capOk) => {
        if (!capOk) return;
        return switchMemory(name)
          .then(() => toast.success(t("settings.memorySwitched", { name, previous })))
          .catch((e: Error) => toast.error(t("settings.saveFailed", { detail: errorText(e) })));
      });
    });
  };
  // the stale pointer's repair: the record moves to the head, the memory the directory always holds
  const keepHere = () =>
    ensureSessions([headMemory], (ok) => {
      if (!ok) return;
      void ensurePayerCap([headMemory]).then((capOk) => {
        if (!capOk) return;
        return switchMemory(headMemory)
          .then(() => toast.success(t("settings.recordKept", { name: headMemory })))
          .catch((e: Error) => toast.error(t("settings.saveFailed", { detail: errorText(e) })));
      });
    });
  return (
    <>
      <PageHeader title={t("wallet.settings.title")} description={t("wallet.settings.description")} />
      {!signedIn ? (
        <SignedOut what="signedOut.settings" />
      ) : (
        <Card>
          <CardContent className="flex flex-col gap-6">
            <div className="grid gap-2">
              <Label htmlFor="default-runner">{t("wallet.settings.defaultRunner")}</Label>
              <Select value={settings.defaultRunner} onValueChange={(v) => save({ defaultRunner: v })}>
                <SelectTrigger id="default-runner" className="w-full sm:max-w-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {runners.map((e) => (
                    <SelectItem key={e.name} value={e.name}>
                      {e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="default-memory">{t("wallet.settings.defaultMemory")}</Label>
              <Select value={settings.defaultMemory} onValueChange={switchTo}>
                <SelectTrigger id="default-memory" className="w-full sm:max-w-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {memories.map((e) => (
                    <SelectItem key={e.name} value={e.name}>
                      {e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {settingsProblem && (
                <div className="flex flex-col items-start gap-2">
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    {t("wallet.settings.memoryGone", { name: settingsProblem.pointer, head: headMemory })}
                  </p>
                  <Button variant="outline" size="sm" onClick={keepHere}>
                    {t("wallet.settings.keepHere")}
                  </Button>
                </div>
              )}
              <p className="text-xs text-muted-foreground">{t("wallet.settings.defaultMemoryHint")}</p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="default-model">{t("wallet.settings.defaultModel")}</Label>
              <Select value={settings.defaultModel} onValueChange={(v) => save({ defaultModel: v })}>
                <SelectTrigger id="default-model" className="w-full sm:max-w-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {models.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.display}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="default-effort">{t("wallet.settings.defaultEffort")}</Label>
              <Select value={settings.defaultEffort} onValueChange={(v) => save({ defaultEffort: v })}>
                <SelectTrigger id="default-effort" className="w-full sm:max-w-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EFFORTS.map((e) => (
                    <SelectItem key={e} value={e} className="font-mono">
                      {e}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{t("effort.hint")}</p>
            </div>
            <div className="grid gap-2">
              <Label>{t("wallet.settings.theme")}</Label>
              <Tabs value={theme} onValueChange={(v) => setTheme(v as "light" | "dark")}>
                <TabsList>
                  <TabsTrigger value="light">
                    <SunIcon />
                    {t("wallet.settings.light")}
                  </TabsTrigger>
                  <TabsTrigger value="dark">
                    <MoonIcon />
                    {t("wallet.settings.dark")}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardContent>
        </Card>
      )}
      {signedIn && <SettingsRecordCard />}
    </>
  );
}
